import React, {useCallback, useMemo} from 'react';
import {RequirementUtilsService} from '../RequirementUtils.service';
import {
	useCreateRequirementMutation,
	useDeleteManyRequirementsMutation,
	useDeleteRequirementMutation,
	useGetRequirementByIdLazyQuery,
} from '../hooks/requirements.generated';
import {useCloneRequirement} from '../hooks/useCloneRequirement';
import {useRequirementsVersions} from '../hooks/useRequirementsVersions';
import {useGetConvolutesQuery} from 'features/AdminSection/hooks/convolutes.generated';
import {ConvoluteType, Requirement, RequirementStatus} from 'types';
import {useGetGdprRuleQuery} from 'features/AdminSection/hooks/gdprRules.generated';
import {SKIP_DISABLED_QUERY} from 'constants/disabledQueries';
import {useUserContext} from 'authentication/UserContext';
import {
	UpdateRequirement,
	UpdateRequirementAttachments,
	useRequirementAttachmentsUpdater,
	useRequirementsFormElementsRenderer,
	useRequirementUpdater,
} from '../hooks/requirementFormHooks';
import {createHrefFromRequirement} from '../requirementLinks.utils';
import {useNavigate} from 'react-router-dom';
import {PanelType} from 'components/FluentUICompat';
import {useRequirementsTranslation} from '../hooks/useRequirementsTranslation';
import {useSentToApprovalCommand} from '../hooks/useSentToApprovalCommand';
import {FaqCommand} from 'components/Faq/FaqCommand';
import {NewRequirementVersionPanel} from './NewRequirementVersionPanel';
import {FaqPanelMode} from 'components/Faq/FaqPanel';
import {InfinityPage} from 'components/InfinityPage/InfinityPage';
import {RequirementFromDetailsPage} from '../RequirementDetailsPage/requirementDetailsPage.types';
import {useEntityContext} from 'components/EntityPage/EntityContext';

const {createCommonCreationInputFields} = new RequirementUtilsService();

const allowedDeleteStatuses = new Set([
	RequirementStatus.Draft,
	RequirementStatus.ReadyForApproval,
	RequirementStatus.Modified,
]);

export const RequirementsPageRequirementForm: React.FC = () => {
	const {t} = useRequirementsTranslation();

	const [getRequirmentOriginal] = useGetRequirementByIdLazyQuery();
	const [createRequirementMutation] = useCreateRequirementMutation();
	const [deleteRequirementMutation] = useDeleteRequirementMutation();
	const [deleteManyRequirementsMutation] = useDeleteManyRequirementsMutation();
	const {cloneRequirement} = useCloneRequirement();
	const {hasEditableChildren} = useRequirementsVersions();

	const navigate = useNavigate();
	const onViewRequirement = useCallback(
		(req: Requirement) => {
			const href: string = createHrefFromRequirement(req);
			return navigate(href);
		},
		[navigate],
	);

	const preventUserFromModification = (selectedItem: Requirement) => {
		const vexClusterMatch = selectedItem.vexClusters.some(vc =>
			myVexClusters.some(mvc => mvc.id === vc.id),
		);

		const canVexSupportEdit =
			isVexSupport &&
			selectedItem.status === RequirementStatus.ReadyForApproval;

		return !isAdmin && !vexClusterMatch && !canVexSupportEdit;
	};

	const refetchQueries = useMemo(() => [], []);

	const {isAdmin, isVex, isVexSupport, myVexClusters} = useUserContext();

	const isDeleteDisabledForItem = useCallback(
		(item: Requirement) => {
			return (
				!item.vexClusters.some(vc =>
					myVexClusters.some(mvc => mvc.id === vc.id),
				) || !allowedDeleteStatuses.has(item.status)
			);
		},
		[myVexClusters],
	);

	const disableDelete = useCallback(
		(selectedItem: Requirement) => isDeleteDisabledForItem(selectedItem),
		[isDeleteDisabledForItem],
	);

	const disableDeleteMany = useCallback(
		(selectedItems: Requirement[]) => {
			return selectedItems.some(isDeleteDisabledForItem);
		},
		[isDeleteDisabledForItem],
	);

	const disableEdit = useCallback(
		(selectedItem: Requirement) => {
			return (
				hasEditableChildren(selectedItem) ||
				preventUserFromModification(selectedItem) ||
				selectedItem.status === RequirementStatus.Approval ||
				selectedItem.status === RequirementStatus.Interim ||
				selectedItem.status === RequirementStatus.InterimCompleted
			);
		},
		[isAdmin, isVexSupport, myVexClusters],
	);

	const {data: convolutes} = useGetConvolutesQuery({skip: SKIP_DISABLED_QUERY});
	const convolute = useMemo(
		() =>
			convolutes?.convolutes?.find(
				c => c.convoluteType === ConvoluteType.Requirement,
			),
		[convolutes],
	);

	const {data: gdprRule} = useGetGdprRuleQuery({
		variables: {
			id: convolute?.gdprRule?.id ?? '',
		},
		skip: SKIP_DISABLED_QUERY,
	});

	const rule = useMemo(() => gdprRule?.gdprRule, [gdprRule]);

	const createRequirement = useCallback(
		async (requirement: Requirement) => {
			if (requirement.status === RequirementStatus.Final) {
				const {data} = await getRequirmentOriginal({
					variables: {id: requirement.id},
				});
				const origComparableRequirement = data?.requirement as Requirement;
				const res = await cloneRequirement(
					requirement as Requirement,
					refetchQueries,
					rule,
					origComparableRequirement,
				);
				return res?.data?.createRequirement?.requirement;
			}

			const res = await createRequirementMutation({
				variables: {
					input: createCommonCreationInputFields(requirement, rule),
				},
				refetchQueries,
			});
			navigate(0);
			return res.data?.createRequirement?.requirement;
		},
		[cloneRequirement, createRequirementMutation, refetchQueries, rule],
	);

	const updateRequirement: UpdateRequirement =
		useRequirementUpdater(refetchQueries);

	const updateRequirementFinal = async (item: Requirement) => {
		await updateRequirement(item);
		navigate(0);
	};

	const deleteRequirement = useCallback(
		async (id: string): Promise<void> => {
			await deleteRequirementMutation({
				variables: {
					input: {id},
				},
				refetchQueries,
			});
		},
		[deleteRequirementMutation, refetchQueries],
	);

	const deleteManyRequirements = useCallback(
		async (ids: string[]): Promise<void> => {
			await deleteManyRequirementsMutation({
				variables: {
					input: {ids},
				},
				refetchQueries,
			});
			navigate(0);
		},
		[deleteManyRequirementsMutation, refetchQueries, navigate],
	);

	const {renderFormElements} =
		useRequirementsFormElementsRenderer<Requirement>();

	const updateAttachments: UpdateRequirementAttachments =
		useRequirementAttachmentsUpdater();

	const {selectedItems} = useEntityContext<Requirement>();

	const selectedRequirements = useMemo(() => {
		return selectedItems as unknown as RequirementFromDetailsPage[];
	}, [selectedItems]);

	const vexClusterMatchForSelected = useMemo(() => {
		return (
			selectedRequirements.length > 0 &&
			selectedRequirements.every(req =>
				req?.vexClusters?.some(vc =>
					myVexClusters?.some(mvc => mvc.id === vc.id),
				),
			)
		);
	}, [selectedRequirements, myVexClusters]);

	const {workflowDialog} = useSentToApprovalCommand({
		requirements: selectedRequirements,
		vexClusterMatch: vexClusterMatchForSelected,
	});

	const item: Requirement[] = [];
	return (
		<>
			{workflowDialog}
			<NewRequirementVersionPanel />
			<FaqCommand
				faqState={{
					mode: FaqPanelMode.EntityList,
					entity: selectedItems[0] as any,
					showPanel: false,
				}}
			/>
			<InfinityPage
				items={item}
				entityDisplayName={t('Requirements')}
				entityHeaderText={t('RequirementsHeader')}
				createEntity={createRequirement}
				updateEntity={updateRequirementFinal}
				deleteEntity={deleteRequirement}
				onDeleteMany={deleteManyRequirements}
				updateEntityAttachments={updateAttachments}
				additionalColumns={[]}
				renderAdditionalFormElements={renderFormElements}
				hideCreate={!isAdmin && !isVex && !isVexSupport}
				hideEdit={!isAdmin && !isVex}
				hideDelete={isVexSupport || (!isAdmin && !isVex)}
				panelType={PanelType.medium}
				onViewItem={onViewRequirement}
				confirmUpdate
				refetchQueries={refetchQueries}
				disableDelete={disableDelete}
				hideDeleteMany={false}
				disableEdit={disableEdit}
				disableDeleteMany={disableDeleteMany}
				withNameColumn={false}
				withNameColumnInPanel={true}
				customTranslations={{
					deleteSubtextTranslationKey: 'RequirementSubtext',
					deleteManySubtextTranslationKey: 'DeleteManyRequirementSubtext',
				}}
			/>
		</>
	);
};
